
import { useEffect, useState, useMemo } from 'react';
import { useOfflineStatus } from './OfflineContext';
import { WifiOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const OfflineIndicator = () => {
  const { isOffline } = useOfflineStatus();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  
  // Check for pending changes in local storage
  useEffect(() => {
    const checkPendingChanges = () => {
      try {
        const pendingData = localStorage.getItem('offline_dashboard_data');
        setHasPendingChanges(!!pendingData && pendingData !== '[]');
      } catch (e) {
        console.error('Error checking pending changes', e);
      }
    };
    
    // Check initially
    checkPendingChanges();
    
    // Set up an interval to check periodically when offline
    let interval: number | null = null;
    if (isOffline) {
      interval = window.setInterval(checkPendingChanges, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOffline]);

  // Show/hide the indicator with animation
  useEffect(() => {
    if (isOffline && !isDismissed) {
      setIsVisible(true);
    } else {
      // Use a timer to allow the animation to complete
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOffline, isDismissed]);

  // Reset dismissed state when coming back online
  useEffect(() => {
    if (!isOffline) {
      setIsDismissed(false);
    }
  }, [isOffline]);

  // Memoize the message to avoid recreating it on every render
  const offlineMessage = useMemo(() => {
    if (hasPendingChanges) {
      return "You're offline. Your changes are being saved locally and will be synced when you reconnect.";
    }
    return "You're offline. The app will continue to work with limited functionality.";
  }, [hasPendingChanges]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 left-0 right-0 z-50 mx-auto w-11/12 max-w-md transform transition-all duration-300",
        isOffline ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      )}
    >
      <div className="bg-red-50 border-2 border-red-400 shadow-lg rounded-lg p-4 flex items-start gap-3">
        <div className="bg-red-100 p-2 rounded-full">
          <WifiOff className="h-6 w-6 text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-red-800 font-medium text-sm">You're offline</h3>
          <p className="text-red-700 text-xs mt-0.5">
            {offlineMessage}
          </p>
        </div>
        <button 
          onClick={() => setIsDismissed(true)}
          className="text-red-600 hover:text-red-800 transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
